import React, { useState, useEffect, useRef, useMemo } from "react";
import { MapPin, X } from "lucide-react";
import { AU_CITIES } from "@/data/aussieCities";
import { STATE_COLORS } from "@/lib/constants";

export function CityAutocomplete({ value, onChange, placeholder }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    // Match by name (starts with > contains) OR by postcode
    const starts = [];
    const contains = [];
    const postcode = [];
    for (const c of AU_CITIES) {
      const n = c.name.toLowerCase();
      if (n.startsWith(q)) starts.push(c);
      else if (n.includes(q)) contains.push(c);
      else if (c.postcode.startsWith(q) && /^\d/.test(q)) postcode.push(c);
    }
    return [...starts, ...contains, ...postcode].slice(0, 8);
  }, [query]);

  const selectCity = (city) => {
    const formatted = `${city.name}, ${city.state} ${city.postcode}`;
    setQuery(formatted);
    onChange(formatted);
    setOpen(false);
  };

  const clear = () => {
    setQuery("");
    onChange("");
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      selectCity(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="city-autocomplete">
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D95D39] pointer-events-none z-10" />
        <input
          className="input !pl-11 !pr-10"
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            onChange(e.target.value);
            setActiveIdx(0);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          data-testid="location-input"
        />
        {query && (
          <button
            type="button"
            onClick={clear}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-[#F2EBE1] flex items-center justify-center text-[#78716C]"
            data-testid="location-clear-btn"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 left-0 right-0 mt-2 bg-white border border-[#EAE5DB] rounded-2xl shadow-2xl max-h-[320px] overflow-auto"
          data-testid="city-suggestions"
        >
          {suggestions.map((c, idx) => {
            const stateColor = STATE_COLORS[c.state] || "#57534E";
            return (
              <button
                key={`${c.name}-${c.postcode}`}
                type="button"
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => selectCity(c)}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                  idx === activeIdx ? "bg-[#FAF6F0]" : "bg-white"
                } hover:bg-[#FAF6F0] border-b border-[#F2EBE1] last:border-0`}
                data-testid={`city-option-${c.name.replace(/\s+/g, "-")}`}
              >
                <MapPin className="w-4 h-4 flex-shrink-0" style={{ color: stateColor }} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[#292524] text-sm truncate">{c.name}</div>
                  <div className="text-xs text-[#78716C]">
                    <span
                      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-bold mr-1.5 text-white"
                      style={{ backgroundColor: stateColor }}
                    >
                      {c.state}
                    </span>
                    {c.postcode}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
